import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SchoolApi } from '../../api/client';
import SectionListPanel from './components/SectionListPanel';
import SectionDetailPanel from './components/SectionDetailPanel';
import { PageHeader, PanelCard, EmptyState, Segmented, Spinner } from '../../components/ui';
import { FaUsers } from '../../components/ui/icons';
import { gradeCompact } from '../../lib/gradeLabel';

export default function SectionsPage() {
  const [gradeId, setGradeId] = useState<number | null>(null);
  const [sectionId, setSectionId] = useState<number | null>(null);

  const gradesQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
  });
  const grades = gradesQ.data?.grades ?? [];
  const grade = grades.find((g) => g.id === gradeId) ?? null;

  useEffect(() => {
    if (gradeId == null && grades.length > 0) setGradeId(grades[0].id);
  }, [gradeId, grades]);

  useEffect(() => { setSectionId(null); }, [gradeId]);

  const subtitle = grade
    ? `${grade.name} · ${grade.sections.length} sections · ${grade.student_count} students. Homeroom teachers, rosters and groups.`
    : 'Homeroom teachers, rosters and groups, grade by grade.';

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="Sections"
        subtitle={subtitle}
        actions={grades.length > 0 ? (
          <div className="flex max-w-full flex-wrap items-center gap-2.5">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Grade</span>
            <Segmented
              label="Grade"
              scroll
              options={grades.map((g) => ({ key: String(g.id), label: gradeCompact(g.name), title: g.name }))}
              value={gradeId == null ? '' : String(gradeId)}
              onChange={(k) => setGradeId(Number(k))}
            />
          </div>
        ) : undefined}
      />

      {gradesQ.isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="md" /></div>
      ) : gradeId == null ? (
        <PanelCard>
          <EmptyState
            icon={<FaUsers size={18} strokeWidth={1.8} />}
            title={grades.length === 0 ? 'No grades yet' : 'Pick a grade to manage sections'}
            body={grades.length === 0 ? 'Add a grade in Classes first, then manage its sections here.' : 'Select a grade from the picker above to view and manage its sections.'} />
        </PanelCard>
      ) : (
        <div className="grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]">
          <SectionListPanel
            gradeId={gradeId}
            gradeName={grade?.name}
            selectedId={sectionId}
            onSelect={setSectionId}
          />
          {sectionId == null ? (
            <PanelCard>
              <EmptyState
                icon={<FaUsers size={18} strokeWidth={1.8} />}
                title="Select a section to manage it"
                body="Pick a section from the list on the left, or create a new one." />
            </PanelCard>
          ) : (
            <SectionDetailPanel
              gradeId={gradeId}
              gradeName={grade?.name}
              sectionId={sectionId}
              onDeleted={() => setSectionId(null)}
            />
          )}
        </div>
      )}
    </>
  );
}
